import {
  T,
  formatMoney,
  lunchMealDescription,
  tt,
  type LunchDay,
  type LunchLinkedProduct,
  type LunchMeal,
  type LunchSettings,
} from '@agos/shared'

import {
  printFullDate,
  printLogo,
  printWeekPages,
  printWeekRange,
  type PrintWeekPage,
} from './print-week'

/**
 * The printed week, as legacy prints it - `FRONTEND.md` §9.377, FB-672.
 *
 * **The owner asked for *1:1***, and §9.132 had closed point 16b after fixing what the printer was
 * *told* - `@page A4`, the 10 mm margin, `print-color-adjust` - without comparing the layout. It was
 * a different document: legacy prints **two pages**, Monday to Wednesday and then the rest, each
 * with its own full header, and the days as a **table** rather than a stack of sections.
 *
 * Every dimension here is legacy's own (`lunch-menu.tsx:608-805`), including the ones that look
 * arbitrary: `13%/87%` columns, a 3 px accent rule under the header, 9.5 px meal names, the 6 px
 * *Polievka* pill. They are arbitrary - they are also what the sheet on the restaurant wall looks
 * like, and *1:1* is the request.
 *
 * **Day mode is not this.** Legacy's day print is its own template with its own scale-to-fit, and
 * §9.132 records that measuring pass as unbuilt; `LunchPrintView` still draws it.
 */
const ACCENT = '#c0392b'
const ACCENT_LIGHT = '#f9eae8'

/** `print-color-adjust`, on every element that carries the accent. Browsers drop it otherwise. */
const EXACT = {
  WebkitPrintColorAdjust: 'exact',
  printColorAdjust: 'exact',
} as const

function soups(meals: readonly LunchMeal[]): LunchMeal[] {
  return meals.filter((meal) => meal.type === 'soup')
}

function mains(meals: readonly LunchMeal[]): LunchMeal[] {
  return meals.filter((meal) => meal.type !== 'soup')
}

function allergens(meal: LunchMeal): string {
  return meal.allergens.length === 0 ? '' : `(${meal.allergens.join(',')})`
}

function PageHeader({
  settings,
  logoUrl,
  dateRange,
  pageLabel,
}: {
  settings: LunchSettings
  logoUrl: string | null
  dateRange: string
  pageLabel: string
}) {
  return (
    <div
      className="flex items-center justify-between"
      style={{
        paddingBottom: 9,
        marginBottom: 8,
        borderBottom: `3px solid ${ACCENT}`,
        ...EXACT,
      }}
    >
      <div className="flex items-center" style={{ gap: 10 }}>
        {logoUrl !== null && (
          <img src={logoUrl} alt="" style={{ height: 52, width: 'auto', objectFit: 'contain' }} />
        )}
        <div>
          <div style={{ fontSize: 21, fontWeight: 900, letterSpacing: '-0.5px', lineHeight: 1.1 }}>
            {settings.restaurantName}
          </div>
          {settings.restaurantTagline !== '' && (
            <div style={{ fontSize: 9, color: '#666', fontStyle: 'italic', marginTop: 1 }}>
              {settings.restaurantTagline}
            </div>
          )}
          <div className="flex flex-wrap" style={{ gap: 10, marginTop: 3 }}>
            {settings.restaurantAddress !== '' && (
              <span style={{ fontSize: 8, color: '#555' }}>📍 {settings.restaurantAddress}</span>
            )}
            {settings.restaurantPhone !== '' && (
              <span style={{ fontSize: 8, color: '#555' }}>📞 {settings.restaurantPhone}</span>
            )}
            {settings.restaurantEmail !== '' && (
              <span style={{ fontSize: 8, color: '#555' }}>✉ {settings.restaurantEmail}</span>
            )}
          </div>
        </div>
      </div>

      <div style={{ textAlign: 'center', flex: '0 0 auto', padding: '0 20px' }}>
        <div
          style={{ fontSize: 18, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 4 }}
          data-print-title
        >
          <T>Obedové Menu</T>
        </div>
        {dateRange !== '' && (
          <div
            style={{ fontSize: 10, color: '#555', marginTop: 3, fontWeight: 600 }}
            data-print-range
          >
            {dateRange}
          </div>
        )}
        <div
          style={{ fontSize: 8, color: '#888', marginTop: 2, fontWeight: 600 }}
          data-print-page-label
        >
          {pageLabel}
        </div>
        <div style={{ fontSize: 7, color: '#aaa', marginTop: 1, fontStyle: 'italic' }}>
          <T>Alergény podľa nar. EÚ č. 1169/2011</T>
        </div>
      </div>

      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div
          style={{
            fontSize: 8,
            color: '#999',
            textTransform: 'uppercase',
            letterSpacing: 1,
            marginBottom: 2,
          }}
        >
          <T>Výdaj obedov</T>
        </div>
        <div style={{ fontSize: 22, fontWeight: 900, color: ACCENT, ...EXACT }}>
          {settings.timeFrom} – {settings.timeTo}
        </div>
      </div>
    </div>
  )
}

function DayRow({
  day,
  weekStartDate,
  isLast,
  products,
}: {
  day: LunchDay
  weekStartDate: string | undefined
  isLast: boolean
  products: readonly LunchLinkedProduct[]
}) {
  const fullDate = printFullDate(weekStartDate, day.dayKey)
  const theSoups = soups(day.meals)
  const theMains = mains(day.meals)

  return (
    <tr data-print-day={day.dayKey}>
      <td
        style={{
          verticalAlign: 'top',
          padding: '5px 6px 5px 0',
          borderBottom: isLast ? 'none' : '1px solid #e8e8e8',
        }}
      >
        <div
          style={{
            backgroundColor: ACCENT_LIGHT,
            borderLeft: `3px solid ${ACCENT}`,
            borderRadius: '0 2px 2px 0',
            padding: '4px 5px',
            ...EXACT,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 900,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: ACCENT,
              lineHeight: 1.2,
              // §9.386 - a name too long for the column breaks **inside** the badge instead of
              // running out of it. A franchise can rename a day, so the width alone is not a
              // guarantee.
              overflowWrap: 'anywhere',
              ...EXACT,
            }}
            data-print-day-name
          >
            {day.dayName}
          </div>
          {fullDate !== '' && (
            <div style={{ fontSize: 8, color: '#777', marginTop: 1, fontWeight: 600 }}>
              {fullDate}
            </div>
          )}
        </div>
      </td>

      <td
        style={{
          verticalAlign: 'top',
          padding: '5px 0 5px 7px',
          borderBottom: isLast ? 'none' : '1px solid #e8e8e8',
          borderLeft: `2px solid ${ACCENT}22`,
        }}
      >
        {!day.enabled ? (
          <div style={{ fontSize: 9, color: '#aaa', fontStyle: 'italic' }} data-print-closed>
            <T>Zatvorené</T>
          </div>
        ) : day.meals.length === 0 ? (
          <div style={{ fontSize: 9, color: '#ccc', fontStyle: 'italic' }} data-print-empty>
            {/* Hyphens, not legacy's em dashes: the rule in `CLAUDE.md` is absolute and
              * applies to UI strings, parity or not. `em-dash.test.ts` caught this one. */}
            <T>- žiadne jedlá -</T>
          </div>
        ) : (
          <>
            {theSoups.map((soup) => (
              <div
                key={soup.id}
                className="flex items-baseline justify-between"
                style={{ gap: 8, marginBottom: 3 }}
              >
                <span>
                  <span
                    style={{
                      backgroundColor: ACCENT,
                      color: 'white',
                      fontSize: 6,
                      fontWeight: 800,
                      padding: '1px 3px',
                      borderRadius: 2,
                      marginRight: 4,
                      textTransform: 'uppercase',
                      ...EXACT,
                    }}
                  >
                    Polievka
                  </span>
                  <span
                    data-print-dish
                    style={{ fontSize: 11, fontWeight: 600, color: '#2c2c2c', lineHeight: 1.35 }}
                  >
                    <T>{soup.name}</T>
                  </span>
                  {allergens(soup) !== '' && (
                    <span style={{ fontSize: 7, fontWeight: 600, color: '#555', marginLeft: 3 }}>
                      {allergens(soup)}
                    </span>
                  )}
                </span>
                <span
                  style={{ fontWeight: 900, fontSize: 11, whiteSpace: 'nowrap' }}
                  data-print-price
                >
                  {formatMoney(soup.price)}
                </span>
              </div>
            ))}

            {theSoups.length > 0 && theMains.length > 0 && (
              <div style={{ height: 1, backgroundColor: '#efefef', margin: '3px 0', ...EXACT }} />
            )}

            {theMains.map((meal, index) => (
              <div
                key={meal.id}
                className="flex items-baseline justify-between"
                style={{ gap: 8, marginBottom: index < theMains.length - 1 ? 3 : 0 }}
              >
                <span>
                  <span
                    style={{ fontWeight: 900, color: ACCENT, marginRight: 4, fontSize: 10.5, ...EXACT }}
                  >
                    {index + 1}.
                  </span>
                  <span data-print-dish style={{ fontSize: 11, fontWeight: 600, lineHeight: 1.35 }}>
                    <T>{meal.name}</T>
                  </span>
                  {allergens(meal) !== '' && (
                    <span style={{ fontSize: 7, fontWeight: 600, color: '#555', marginLeft: 3 }}>
                      {allergens(meal)}
                    </span>
                  )}
                  {/* §9.401 - the linked product's popis, never the stored copy. */}
                  {lunchMealDescription(meal, products) !== '' && (
                    <div
                      data-print-descr
                      style={{
                        fontSize: 9,
                        fontWeight: 600,
                        color: '#444',
                        fontStyle: 'italic',
                        marginTop: 1,
                        paddingLeft: 14,
                      }}
                    >
                      <T>{lunchMealDescription(meal, products)}</T>
                    </div>
                  )}
                </span>
                <span
                  style={{ fontWeight: 900, fontSize: 11, whiteSpace: 'nowrap' }}
                  data-print-price
                >
                  {formatMoney(meal.price)}
                </span>
              </div>
            ))}
          </>
        )}
      </td>
    </tr>
  )
}

function Page({
  page,
  settings,
  logoUrl,
  dateRange,
  weekStartDate,
  products,
}: {
  page: PrintWeekPage
  settings: LunchSettings
  logoUrl: string | null
  dateRange: string
  weekStartDate: string | undefined
  products: readonly LunchLinkedProduct[]
}) {
  return (
    <div
      style={{ fontFamily: "'Helvetica Neue', Arial, sans-serif", color: '#1a1a1a' }}
      data-print-week-page={page.label}
    >
      <PageHeader
        settings={settings}
        logoUrl={logoUrl}
        dateRange={dateRange}
        pageLabel={page.label}
      />

      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        {/* §9.386, FB-687 - **16 %, not legacy's 13 %.** The owner: *"PONDELOK in tlač in obedové
          * menu is off, it does not fit fully"*. `tableLayout: fixed` plus a single word with no
          * break opportunity means the name does not wrap - it runs past its coloured badge. The
          * arithmetic says 13 % of A4 fits it here, and the margin is one font substitution wide;
          * the mains column gives up 24 px and has it to spare. */}
        <colgroup>
          <col style={{ width: '16%' }} />
          <col style={{ width: '84%' }} />
        </colgroup>
        <tbody>
          {page.days.map((day, index) => (
            <DayRow
              key={day.dayKey}
              day={day}
              weekStartDate={weekStartDate}
              isLast={index === page.days.length - 1}
              products={products}
            />
          ))}
        </tbody>
      </table>

      <div
        className="flex justify-between"
        style={{
          marginTop: 10,
          paddingTop: 6,
          borderTop: '1px solid #e0e0e0',
          fontSize: 7,
          color: '#bbb',
        }}
      >
        <span>
          <T>
            Vyhradzujeme si právo na zmenu jedálneho lístka. • Alergény podľa nariadenia EÚ č.
            1169/2011 (číslo v zátvorkách)
          </T>
        </span>
        <span style={{ whiteSpace: 'nowrap', marginLeft: 8 }}>
          {dateRange !== '' && <span>{dateRange} · </span>}
          {settings.restaurantName}
        </span>
      </div>
    </div>
  )
}

export function LunchPrintWeek({
  days,
  settings,
  weekStartDate,
  themeLogo,
  products = [],
}: {
  days: readonly LunchDay[]
  settings: LunchSettings
  weekStartDate: string | undefined
  /** The brand logo, for the fallback legacy applies to its own bundled default. */
  themeLogo?: string | null
  /** §9.401 - a meal's popis is its linked product's, or there is none. */
  products?: readonly LunchLinkedProduct[]
}) {
  const pages = printWeekPages(days, settings.showWeekends)
  const dateRange = printWeekRange(weekStartDate, settings.showWeekends)
  const logoUrl = printLogo(settings.restaurantLogoUrl, themeLogo ?? null)

  return (
    <>
      {pages.map((page, index) => (
        <div key={page.label}>
          <Page
            page={page}
            settings={settings}
            logoUrl={logoUrl}
            dateRange={dateRange}
            weekStartDate={weekStartDate}
            products={products}
          />
          {/* Legacy's own break, and only between pages: a trailing one prints a blank sheet. */}
          {index < pages.length - 1 && (
            <div style={{ pageBreakAfter: 'always', breakAfter: 'page' }} />
          )}
        </div>
      ))}
    </>
  )
}

export { tt }
